import React, { useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon, GripHorizontalIcon, PanelLeftCloseIcon, PanelLeftOpenIcon, PanelRightCloseIcon, PanelRightOpenIcon } from 'lucide-react';
import type { Lead, LeadScript, LeadSource, PanelSide } from '../types/leadScript';
import { ScriptLineView } from './ScriptLineView';
import { useDraggablePanel } from '../hooks/useDraggablePanel';
interface LeadScriptPanelProps {
  lead: Lead;
  scripts: LeadScript[];
  source: LeadSource;
  onSourceChange: (source: LeadSource) => void;
  side?: PanelSide;
  defaultExpanded?: boolean;
  expandedWidth?: number;
}
const COLLAPSED_WIDTH = 52;
const EASE = [0.23, 1, 0.32, 1] as const;
export function LeadScriptPanel({
  lead,
  scripts,
  source,
  onSourceChange,
  side = 'right',
  defaultExpanded = true,
  expandedWidth = 440
}: LeadScriptPanelProps) {
  const reduceMotion = useReducedMotion();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const panelRef = useRef<HTMLElement>(null);
  const {
    floating,
    isDragging,
    position,
    floatingHeight,
    dock,
    onHandleKeyDown,
    dragHandleProps
  } = useDraggablePanel(panelRef, expandedWidth);
  const isRight = side === 'right';
  const script = scripts.find(s => s.source === source) ?? scripts[0];
  const CollapseIcon = isRight ? PanelRightCloseIcon : PanelLeftCloseIcon;
  const ExpandIcon = isRight ? PanelRightOpenIcon : PanelLeftOpenIcon;
  const collapse = () => {
    setExpanded(false);
    dock();
  };
  const placementClass = floating ? `fixed z-50 rounded-2xl border ${isDragging ? 'shadow-[0_24px_60px_rgba(24,24,27,0.28)]' : 'shadow-[0_16px_44px_rgba(24,24,27,0.2)]'}` : `relative h-full ${isRight ? 'border-l' : 'border-r'} shadow-[0_0_24px_rgba(24,24,27,0.06)]`;
  return <motion.aside ref={panelRef} aria-label="Lead script" initial={false} animate={{
    width: expanded ? expandedWidth : COLLAPSED_WIDTH,
    scale: isDragging ? 1.01 : 1
  }} transition={{
    duration: reduceMotion ? 0 : 0.26,
    ease: EASE,
    scale: {
      duration: 0.15
    }
  }} style={floating ? {
    left: position.x,
    top: position.y,
    height: floatingHeight
  } : undefined} className={`flex shrink-0 flex-col overflow-hidden border-line bg-surface transition-shadow duration-150 ${placementClass}`} data-id="element-2" data-mp="3dup2rspk4h2j">
      <AnimatePresence mode="wait" initial={false} data-id="element-3" data-mp="3dzwont868jjz">
        {expanded ? <motion.div key="expanded" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: reduceMotion ? 0 : 0.14,
        ease: 'easeOut'
      }} className="flex h-full flex-col" style={{
        width: expandedWidth
      }} data-id="element-4" data-mp="3izj50bjaqfx0">
            <header {...dragHandleProps} className={`touch-none select-none bg-accent px-5 pb-5 pt-3 text-white ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`} data-id="element-5" data-mp="139g121ay7y9y">
              <div className="mb-3 flex items-center justify-between" data-id="element-6" data-mp="2rpy0knmzwnmb">
                <button type="button" onKeyDown={onHandleKeyDown} aria-label="Move lead script. Use arrow keys to reposition, Escape to dock." className={`-ml-1.5 inline-flex items-center gap-1.5 rounded-md bg-white/15 px-2 py-1 text-xs font-semibold text-white transition-colors duration-150 hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`} data-id="element-7" data-mp="2ns0ehtoiuhn5">
                  <GripHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-8" data-mp="3fh3j5akblu0b" />
                  {floating ? 'Drag to move' : 'Drag to pop out'}
                </button>
                <button type="button" data-no-drag onClick={collapse} aria-label={floating ? 'Collapse and dock lead script' : 'Collapse lead script'} title={floating ? 'Collapse and dock' : 'Collapse'} aria-expanded={true} className="-mr-1.5 cursor-pointer rounded-md p-1.5 text-white/90 transition-colors duration-150 hover:bg-white/15 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-9" data-mp="1q4i7anaidnl9">
                  <CollapseIcon className="h-[18px] w-[18px]" data-id="element-10" data-mp="13vky5x9gyyal" />
                </button>
              </div>
              <div className="flex items-start gap-3" data-id="element-11" data-mp="18tw2jsgzwglf">
                <div className="min-w-0 flex-1" data-id="element-12" data-mp="1drxocl5t2u0l">
                  <p className="text-xs font-semibold text-white/80" data-id="element-13" data-mp="1dr4qk9otreul">Lead Script</p>
                  <h2 className="mt-1 truncate text-xl font-bold leading-tight" data-id="element-14" data-mp="1ijxr0ld9g10v">{script.name}</h2>
                  <p className="mt-1 truncate text-sm text-white/85" data-id="element-15" data-mp="l2z0d7f04xnd">
                    Calling {lead.firstName} {lead.lastName}
                  </p>
                </div>
              </div>

              <label htmlFor="lead-source" className="sr-only" data-id="element-16" data-mp="ii14pzzwvtlt">
                Lead source
              </label>
              <div className="relative mt-4" data-id="element-17" data-mp="dksmjzerjpkk">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted" data-id="element-18" data-mp="8kwffrarri0s">
                  Source
                </span>
                <select id="lead-source" value={script.source} onChange={e => onSourceChange(e.target.value as LeadSource)} onPointerDown={e => e.stopPropagation()} className="w-full cursor-pointer appearance-none rounded-lg border-0 bg-surface py-2.5 pl-[68px] pr-9 text-sm font-semibold text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-accent" data-id="element-19" data-mp="3ofqiuvqwzmn">
                  {scripts.map(s => <option key={s.source} value={s.source} data-id="element-20" data-mp="38133way9h01g">
                      {s.name}
                    </option>)}
                </select>
                <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-21" data-mp="32ej0hn3r5ytx" />
              </div>
            </header>

            <div key={script.source} className="flex-1 overflow-y-auto px-5 pb-10 pt-6" data-id="element-22" data-mp="2tduloq6jbufz">
              <ol data-id="element-23" data-mp="3h3yrn47746a5">
                {script.sections.map((section, index) => {
              const isLast = index === script.sections.length - 1;
              return <li key={section.id} className="relative pb-8 pl-10 last:pb-0" data-id="element-24" data-mp="37ckkqbcx046z" data-mpi={index}>
                      {!isLast && <span className="absolute bottom-0 left-[13px] top-8 w-px bg-line" aria-hidden="true" data-id="element-25" data-mp="3cam652xtcflm" />}
                      <span className="absolute left-0 top-0 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-xs font-bold text-white" aria-hidden="true" data-id="element-26" data-mp="8n3n7kar2dty">
                        {index + 1}
                      </span>
                      <section aria-labelledby={`section-${section.id}`} data-id="element-27" data-mp="rsi5wsmxlwer">
                        <h3 id={`section-${section.id}`} className="flex min-h-[28px] items-center text-base font-bold text-ink" data-id="element-28" data-mp="msvu8nmr4o82">
                          {section.title}
                        </h3>
                        <ul className="mt-3 space-y-3.5" data-id="element-29" data-mp="3r8x1sons1rj8">
                          {section.lines.map((line, lineIndex) => <li key={lineIndex} data-id="element-30" data-mp="42e7r0siiov5" data-mpi={lineIndex}>
                              <ScriptLineView line={line} lead={lead} data-id="element-31" data-mp="40t9b032uls0" />
                            </li>)}
                        </ul>
                      </section>
                    </li>;
            })}
              </ol>
            </div>
          </motion.div> : <motion.button key="collapsed" type="button" onClick={() => setExpanded(true)} aria-label="Expand lead script" aria-expanded={false} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: reduceMotion ? 0 : 0.14,
        ease: 'easeOut'
      }} className="flex h-full w-full flex-col items-center gap-4 bg-accent py-5 text-white transition-colors duration-150 hover:bg-accent-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white" data-id="element-32" data-mp="39voru0uuih4c">
            <ExpandIcon className="h-[18px] w-[18px]" data-id="element-33" data-mp="aj6696youxv2" />
            <span className="text-sm font-bold" style={{
          writingMode: 'vertical-rl',
          transform: isRight ? undefined : 'rotate(180deg)'
        }} data-id="element-34" data-mp="agjz38fpmkdd">
              Lead Script
            </span>
          </motion.button>}
      </AnimatePresence>
    </motion.aside>;
}